import React, {FormEvent} from 'react';
import HomeModalShell from '../../home/components/HomeModalShell';
import {ProfileSocialProvider} from '../constants';
import styles from '../Profile.module.css';

interface ProfileSocialConnectionModalProps {
  isOpen: boolean;
  provider: ProfileSocialProvider | null;
  title: string;
  description: string;
  value: string;
  submitLabel: string;
  loading: boolean;
  error: string | null;
  validationError: string | null;
  onChange: (value: string) => void;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const ProfileSocialConnectionModal: React.FC<ProfileSocialConnectionModalProps> = ({
  isOpen,
  provider,
  title,
  description,
  value,
  submitLabel,
  loading,
  error,
  validationError,
  onChange,
  onClose,
  onSubmit,
}) => {
  if (!isOpen || !provider) {
    return null;
  }

  return (
    <HomeModalShell
      backdropClassName={styles.editorModalBackdrop}
      panelClassName={styles.socialModalCard}
      ariaLabelledBy="profile-social-connection-title"
      ariaDescribedBy="profile-social-connection-description"
      onClose={onClose}
      closeDisabled={loading}
    >
      <div className={styles.editorModalHeader}>
        <div>
          <h2 id="profile-social-connection-title" className={styles.editorModalTitle}>
            {title}
          </h2>
          <p id="profile-social-connection-description" className={styles.editorModalDescription}>
            {description}
          </p>
        </div>
        <button
          type="button"
          className={styles.editorModalClose}
          onClick={onClose}
          disabled={loading}
          aria-label="Close social connection dialog"
        >
          ×
        </button>
      </div>

      <form onSubmit={onSubmit} className={styles.editorModalForm}>
        <label className={styles.editorField}>
          <span className={styles.editorFieldLabel}>Public URL</span>
          <input
            className={styles.editorInput}
            type="url"
            inputMode="url"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder="https://"
            autoFocus
            disabled={loading}
          />
          <span className={styles.editorHint}>
            Paste the public profile link that visitors should open from your Profile page.
          </span>
        </label>

        {validationError ? (
          <div className={`${styles.editorMessage} ${styles.editorMessageValidation}`}>
            {validationError}
          </div>
        ) : null}

        {error ? (
          <div className={`${styles.editorMessage} ${styles.editorMessageError}`}>
            {error}
          </div>
        ) : null}

        <div className={styles.editorModalActions}>
          <button
            type="button"
            className={`${styles.editorModalButton} ${styles.editorModalButtonSecondary}`}
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`${styles.editorModalButton} ${styles.editorModalButtonPrimary}`}
            disabled={loading}
          >
            {loading ? 'Saving...' : submitLabel}
          </button>
        </div>
      </form>
    </HomeModalShell>
  );
};

export default React.memo(ProfileSocialConnectionModal);
